import { useEffect, useState } from "react"

const CHARS_PER_SECOND = 140
// Cap how far the reveal is allowed to lag the fully-received text. When a
// backlog builds (sentences arriving faster than the base rate, or the reveal
// still crawling after `complete`), the rate scales up so the whole backlog
// clears within this window instead of trailing seconds behind under
// already-rendered feedback buttons.
const MAX_CATCHUP_SECONDS = 1.5
// Ignore frame gaps larger than this. A backgrounded tab pauses rAF; on return
// the first `ts - last` can be many seconds, which would otherwise dump the
// entire remainder in a single frame. Clamping it lets the catch-up rate above
// reveal the backlog smoothly over the next few frames instead.
const MAX_FRAME_MS = 100

/**
 * Progressively reveals `target` character-by-character when `enabled`.
 * Each governor-approved sentence arrives as a full chunk; this animates it in
 * so the stream reads like continuous typing rather than sentence-block pops.
 *
 * Once revealing has started it keeps going until the full text is shown, even
 * after `enabled` flips off (the stream finalized) — otherwise finalizing would
 * dump the remaining un-revealed characters all at once. Messages that were
 * never animated (finalized/historical from the start) show in full instantly.
 * A shrinking or replaced target (retract / substitute) snaps down instantly.
 */
export function useTypewriter(target: string, enabled: boolean): string {
  const [engaged, setEngaged] = useState(enabled)
  const [count, setCount] = useState(enabled ? 0 : target.length)

  useEffect(() => {
    if (enabled) setEngaged(true)
  }, [enabled])

  const active = enabled || engaged

  useEffect(() => {
    if (!active) {
      setCount(target.length)
      return
    }
    // Clamp down instantly if the target shrank (retract / substitute).
    setCount((c) => Math.min(c, target.length))

    let raf = 0
    let last: number | null = null
    const step = (ts: number) => {
      if (last === null) last = ts
      const dtMs = Math.min(ts - last, MAX_FRAME_MS)
      last = ts
      let reachedEnd = false
      setCount((c) => {
        const backlog = target.length - c
        // Base smooth rate, accelerated so any backlog clears within
        // MAX_CATCHUP_SECONDS rather than crawling behind the received text.
        const rate = Math.max(CHARS_PER_SECOND, backlog / MAX_CATCHUP_SECONDS)
        const advance = Math.max(1, Math.round((rate * dtMs) / 1000))
        const next = Math.min(target.length, c + advance)
        reachedEnd = next >= target.length
        return next
      })
      if (!reachedEnd) raf = requestAnimationFrame(step)
    }
    raf = requestAnimationFrame(step)
    return () => cancelAnimationFrame(raf)
  }, [target, active])

  return active ? target.slice(0, count) : target
}
